import {
  FloatingFormItem,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from 'antd';
import InvoiceSerieNumberFields from '../fields/InvoiceSerieNumberFields';
import InvoiceTributesDefaultFields from '../fields/InvoiceTributesDefaultFields';
import { CompanyInvoiceSettingsForm } from '@/core/domain/invoice/settings/Settings.types';
import { FormInstance } from 'antd/es/form/Form';
import InvoiceCreateAuthenticationModal from '../InvoiceCreateAuthenticationModal';
import { useDefaultMaxLengthFieldError, useDisclosure } from '@/hooks';
import InvoiceCreateAuthenticationTable from '../InvoiceCreateAuthenticationTable';
import { useCompanyFormContext } from '../../CompaniesFormProvider';
import { TaxAssessmentOptions } from '@/core/domain/invoice/settings/Settings.consts';
import SelectOperationType from '@/components/_common/TaxReform/SelectOperationType';
import TaxationNatureSelect from '@/components/_common/NatureOfOperationSelect';

interface Props {
  configNfs?: number;
  form: FormInstance<CompanyInvoiceSettingsForm>;
  onSubmit: () => void;
  loadingValidate: boolean;
}

const CompanyInvoiceNFs: React.FC<Props> = ({
  configNfs,
  form,
  onSubmit,
  loadingValidate,
}) => {
  const { useInternalInvoiceApiNfs } = useCompanyFormContext();

  const [openModal, actionsModal] = useDisclosure();

  const login = Form.useWatch(['nfs', 'login'], form);
  const password = Form.useWatch(['nfs', 'password'], form);
  const isAutomaticIbptCalculation = Form.useWatch(
    ['nfs', 'automaticIbptCalculation'],
    form,
  );

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  return (
    <>
      <Form.Item name={['nfs', 'id']} hidden>
        <Input />
      </Form.Item>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Typography.Title level={4}>Geral</Typography.Title>

          <Row gutter={[12, 12]}>
            {!useInternalInvoiceApiNfs && (
              <Col>
                <Form.Item
                  name={['nfs', 'automaticIbptCalculation']}
                  valuePropName="checked"
                  getValueFromEvent={({ target }) => {
                    if (target.checked) {
                      form.setFieldsValue({
                        nfs: {
                          generalIbptRate: undefined,
                          stateIbptRate: undefined,
                          municipalIbptRate: undefined,
                          federalIbptRate: undefined,
                        },
                      });
                    }

                    return target.checked;
                  }}
                  noStyle
                >
                  <Checkbox>Cálcular IBPT automático</Checkbox>
                </Form.Item>
              </Col>
            )}

            <Col>
              <Form.Item
                name={['nfs', 'automaticNumbering']}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>Numeração automática</Checkbox>
              </Form.Item>
            </Col>

            <Col>
              <Form.Item
                name={['nfs', 'sendEmail']}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>Enviar e-mail</Checkbox>
              </Form.Item>
            </Col>

            {!useInternalInvoiceApiNfs && (
              <Col>
                <Form.Item
                  name={['nfs', 'breakLineByDefault']}
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Quebrar linha por padrão</Checkbox>
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <Form.Item
                name={['nfs', 'activeNfseNacional']}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>Ativar emissão da NFS-e Nacional</Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={['nfs', 'groupInvoiceItemsNfsBySubArea']}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>
                  Agrupamento dos serviços por área ao gerar a nota fiscal (para
                  serviços de terceiros será incluída a descrição 'Terceiros').
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <FloatingFormItem
                name={['nfs', 'taxAssessmentRegime']}
                label="Regime de Apuração Tributaria"
              >
                <Select options={TaxAssessmentOptions} allowClear />
              </FloatingFormItem>
            </Col>

            {!isAutomaticIbptCalculation && (
              <>
                <Col span={24}>
                  <Typography.Title level={5}>
                    IBPT Simplificado
                  </Typography.Title>

                  <Row gutter={[16, 16]}>
                    <Col>
                      <FloatingFormItem
                        name={['nfs', 'generalIbptRate']}
                        label="Aliquota Ibpt simplicado"
                      >
                        <InputDecimal fractions={4} max={100} />
                      </FloatingFormItem>
                    </Col>
                  </Row>
                </Col>

                <Col span={24}>
                  <Typography.Title level={5}>IBPT Detalhado</Typography.Title>

                  <Row gutter={[16, 16]}>
                    <Col>
                      <FloatingFormItem
                        name={['nfs', 'stateIbptRate']}
                        label="Aliquota estadual"
                      >
                        <InputDecimal fractions={4} max={100} />
                      </FloatingFormItem>
                    </Col>

                    <Col>
                      <FloatingFormItem
                        name={['nfs', 'municipalIbptRate']}
                        label="Aliquota municipal"
                      >
                        <InputDecimal fractions={4} max={100} />
                      </FloatingFormItem>
                    </Col>

                    <Col>
                      <FloatingFormItem
                        name={['nfs', 'federalIbptRate']}
                        label="Aliquota federal"
                      >
                        <InputDecimal fractions={4} max={100} />
                      </FloatingFormItem>
                    </Col>
                  </Row>
                </Col>
              </>
            )}
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={4}>Transmissão</Typography.Title>

          <Row gutter={[12, 12]}>
            <Col span={24}>
              <Form.Item
                name={['nfs', 'deductDiscountItemValue']}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>
                  Deduzir desconto incondicionado do valor bruto da nota
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={['nfs', 'deductUnconditionalDiscountFromTaxBase']}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>
                  Deduzir desconto incondicionado do valor da base de cálculo
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={4}>Dados</Typography.Title>

          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem label="Lote" name={['nfs', 'batch']}>
                <InputNumber style={{ width: '100%' }} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Receita bruta"
                name={['nfs', 'grossRevenue']}
              >
                <InputDecimal maxLength={14} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Lei (Anexo/Tabela)"
                name={['nfs', 'law']}
                rules={[defaultMaxLenghtFieldError(100)]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Data do inicio da atividade"
                name={['nfs', 'startDate']}
              >
                <InputDate />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                label="Cód. Indicador de Operação"
                name={['nfs', 'operationIndicatorCodeDefault']}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                label="Tipo de operação"
                name={['nfs', 'operationTypeDefault']}
              >
                <SelectOperationType allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                label="Natureza da tributação"
                name={['nfs', 'taxationNatureDefault']}
              >
                <TaxationNatureSelect allowClear />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={4}>Tributos</Typography.Title>

          <InvoiceTributesDefaultFields invoiceType={'nfs'} form={form} />
        </Col>

        <Col span={24}>
          <Typography.Title level={4}>Série e Numeração</Typography.Title>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <InvoiceSerieNumberFields configNfs={configNfs} />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={4}>Autenticação prefeitura</Typography.Title>

          <Row gutter={[16, 16]} justify="end">
            <Button type="primary" onClick={() => actionsModal.toggle()}>
              Dados de autenticação
            </Button>

            <Col span={24}>
              <InvoiceCreateAuthenticationTable data={{ login, password }} />
            </Col>
          </Row>
        </Col>
      </Row>

      <InvoiceCreateAuthenticationModal
        open={openModal}
        onCancel={actionsModal.toggle}
        onSubmit={onSubmit}
        loadingValidate={loadingValidate}
        data={{ login, password }}
      />
    </>
  );
};

export default CompanyInvoiceNFs;
